/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Bryan Sheranian <bryan.sheranian@deliciousai.com>
 */

import { cloneDeep, uniqueId } from 'lodash'
import { SetStateAction } from 'react'

function reorder<TItem>(
  list: TItem[],
  startIndex: number,
  endIndex: number,
): TItem[] {
  const result = [...list]
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}
interface ReorderFacingsArgs<T> {
  facings: T[][]
  source: { row: number; column: number }
  destination: { row: number; column: number }
}

const handleReorderItem = <T>({
  facings,
  source,
  destination,
}: ReorderFacingsArgs<T>): T[][] => {
  const current: T[] = [...facings[source.row]]
  const next: T[] = [...facings[destination.row]]
  const target: T = current[source.column]

  if (source.row === destination.row) {
    const reordered: T[] = reorder(current, source.column, destination.column)
    return [
      ...facings.slice(0, source.row),
      reordered,
      ...facings.slice(source.row + 1),
    ]
  }

  current.splice(source.column, 1)
  next.splice(destination.column, 0, target)

  const result: T[][] = facings.map((shelf, index) => {
    if (index === source.row) {
      return current
    } else if (index === destination.row) {
      return next
    }
    return shelf
  })

  return result.filter(shelf => shelf.length > 0)
}

const handleDeleteShelves = <T>({
  indices,
}: {
  indices: number[]
}): SetStateAction<T[][]> => {
  return prev => prev.filter((_, index) => !indices.includes(index))
}

const handleDuplicateShelves = <T>({
  indices,
}: {
  indices: number[]
}): SetStateAction<T[][]> => {
  return prev => {
    const newPlanogram = cloneDeep(prev)
    for (const index of indices) {
      newPlanogram.splice(
        index + 1,
        0,
        [...prev[index]].map(item => ({ ...item, id: uniqueId() })),
      )
    }
    return newPlanogram
  }
}

const handleDeleteItems = <T>({
  items,
}: {
  items: { rowIndex: number; index: number }[]
}): SetStateAction<T[][]> => {
  return prev =>
    prev
      .map((shelf, shelfIndex) =>
        shelf.filter(
          (_, index) =>
            !items.some(
              ({ rowIndex, index: itemIndex }) =>
                rowIndex === shelfIndex && itemIndex === index,
            ),
        ),
      )
      .filter(shelf => !!shelf.length)
}

const handleDuplicateItems = <T>({
  items,
}: {
  items: { rowIndex: number; index: number }[]
}): SetStateAction<T[][]> => {
  return prev => {
    const newPlanogram = cloneDeep(prev)
    const offsetByRow: Record<number, number> = {}
    items.forEach(({ rowIndex, index }) => {
      if (!offsetByRow[rowIndex]) {
        offsetByRow[rowIndex] = 0
      }
      const adjustedIndex = index + offsetByRow[rowIndex]
      const itemToDuplicate = newPlanogram[rowIndex][adjustedIndex]
      const duplicatedItem = { ...itemToDuplicate, id: uniqueId() }
      newPlanogram[rowIndex].splice(adjustedIndex + 1, 0, duplicatedItem)
      offsetByRow[rowIndex]++
    })
    return newPlanogram
  }
}

const handleAppendFacings = <T extends { id: string }>({
  targetShelfIndices,
  facings,
}: {
  targetShelfIndices: number[]
  facings: T[]
}): SetStateAction<T[][]> => {
  return prev => {
    if (!facings.length) {
      return prev
    }

    const newPlanogram = cloneDeep(prev)

    for (const shelfIndex of targetShelfIndices) {
      if (!newPlanogram[shelfIndex]) {
        newPlanogram[shelfIndex] = []
      }

      const facingsToAppend = facings.map(facing => ({
        ...facing,
        id: uniqueId(),
      }))

      newPlanogram[shelfIndex].push(...facingsToAppend)
    }

    return newPlanogram
  }
}

export const EditPlanogramHelpers = {
  handleReorderItem,
  handleDeleteShelves,
  handleDuplicateShelves,
  handleDeleteItems,
  handleDuplicateItems,
  handleAppendFacings,
}
